import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, EMPTY, Observable, Subscription, catchError, defer, finalize, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';

function formatDates(item: any): any {
  if (item.date) {
    item.date = moment(item.date, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY');
  }
  return item;
}

@Injectable({
  providedIn: 'root'
})
export class TaskService {
  private apiUrl = 'api';
  private tasksSubject = new BehaviorSubject<any[]>([]);

  /**
   * The practice's whole task list, loaded when a screen first shows it rather than on every practice switch.
   * A switch while a screen shows it loads the new practice's list. It is null until the practice's list has
   * arrived, so a screen can tell "loading" from "no tasks".
   */
  tasks$ = defer(() => {
    this.taskListWatchers++;
    this.ensureTaskList();
    return this.tasksSubject.pipe(
      map(tasks => (this.taskListReady ? tasks : null)),
      finalize(() => this.taskListWatchers--),
    );
  });
  private taskListWatchers = 0;
  private taskListFor: string | null = null;
  private taskListReady = false;
  private taskListLoad?: Subscription;

  private monthTasksSubject = new BehaviorSubject<any[] | null>(null);
  private selectedMonthSubject = new BehaviorSubject<{ year: number, month: number } | null>(null);

  monthTasks$ = this.monthTasksSubject.asObservable();
  selectedMonth$ = this.selectedMonthSubject.asObservable();

  groupId: any;
  currentPage = 1;
  pageSize = 500;
  isLoading = false;

  constructor(private http: HttpClient, private groupService: GroupService, private socketService: SocketService) {

    this.setupRealTimeUpdates();

    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          this.currentPage = 1;
          this.taskListReady = false;
          this.tasksSubject.next([]);
          this.monthTasksSubject.next([]);
          this.taskListLoad?.unsubscribe();
          this.isLoading = false;
          this.taskListFor = null;
          if (this.taskListWatchers > 0) this.ensureTaskList();
          return this.loadGroupTasks();
        }
        return EMPTY;
      })
    ).subscribe();

  }

  private setupRealTimeUpdates(): void {
    // Listen for a new item being added
    this.socketService.listen('taskAdded', 'task-service').subscribe((newData) => {
      //const formatted = formatDates(newData);
      const current = this.tasksSubject.getValue();
      //this.tasksSubject.next([...current, formatted]); // Add the new item
      this.tasksSubject.next([newData, ...current]); // Add the new item

      this.updateMonthTasks(newData);
    });

    // Listen for updates
    this.socketService.listen('taskUpdated', 'task-service').subscribe((updated) => {
      //const formatted = formatDates(updated);

      // Update in the main list
      const current = this.tasksSubject.getValue();
      //const index = current.findIndex(p => p.id === formatted.id);
      const index = current.findIndex(p => p.id === updated.id);
      if (index !== -1) {
        //current[index] = formatted;
        current[index] = updated;
        this.tasksSubject.next([...current]); // Replace the item in the array
      }

      this.updateMonthTasks(updated);
    });

    // Listen for an item being deleted
    this.socketService.listen('taskDeleted', 'task-service').subscribe((deleted) => {
      //const formatted = formatDates(deleted);

      const current = this.tasksSubject.getValue();
      //const updated = current.filter(p => p.id !== formatted.id);
      const updated = current.filter(p => p.id !== deleted.id);
      this.tasksSubject.next(updated); // Remove the deleted item

      this.removeFromMonthTasks(deleted.id);
    });

    this.socketService.listen('patientUpdated', 'task-service').subscribe((updatedPatient) => {
      // Get the current value of tasksSubject
      const current = this.tasksSubject.getValue();
      const currentMonth = this.monthTasksSubject.getValue();

      // Map through the current tasks list to update the specific patient's details
      const updatedTasks = current.map(item => {
        if (item.Patient.id === updatedPatient.id) {
          // Update the diagnosis for the specific patient
          return {
            ...item,
            Patient: {
              ...item.Patient,
              name: updatedPatient.name,
              surname: updatedPatient.surname,
              patientNumber: updatedPatient.patientNumber,
              requiresValidation: updatedPatient.requiresValidation,
              requiresICDValidation: updatedPatient.requiresICDValidation,
              wasSelfBoarded: updatedPatient.wasSelfBoarded,
              status: updatedPatient.status,
            }
          };
        }
        return item;
      });

      const updatedMonth = currentMonth?.map(item => {
        if (item.Patient.id === updatedPatient.id) {
          return {
            ...item,
            Patient: {
              ...item.Patient,
              name: updatedPatient.name,
              surname: updatedPatient.surname,
              patientNumber: updatedPatient.patientNumber,
              requiresValidation: updatedPatient.requiresValidation,
              requiresICDValidation: updatedPatient.requiresICDValidation,
              wasSelfBoarded: updatedPatient.wasSelfBoarded,
              status: updatedPatient.status,
            }
          };
        }
        return item;
      });


      if (updatedMonth) {
        this.monthTasksSubject.next(updatedMonth);
      }

      // Update the tasksSubject with the new list
      this.tasksSubject.next(updatedTasks);
    });
  }

  private updateMonthTasks(details: any) {
    const selectedMonth = this.selectedMonthSubject.getValue();
    if (selectedMonth && details.dueDate) {
      const itemDate = new Date(details.dueDate);
      if (
        itemDate.getFullYear() === selectedMonth.year &&
        itemDate.getMonth() + 1 === selectedMonth.month
      ) {
        const currentMonthItems = this.monthTasksSubject.getValue();
        const existingItemIndex = currentMonthItems!.findIndex(
          (item) => item.id === details.id
        );

        if (existingItemIndex !== -1) {
          const updated = [...currentMonthItems!];
          updated[existingItemIndex] = details;
          this.monthTasksSubject.next(updated);
        } else {
          // Add new appointment
          const updated = [...currentMonthItems!, details];
          this.monthTasksSubject.next(updated);
        }
      }
    }
  }

  private removeFromMonthTasks(id: string) {
    const current = this.monthTasksSubject.getValue();
    const updated = current!.filter(item => item.id !== id);
    this.monthTasksSubject.next(updated);
  }

  loadGroupTasks(): Observable<any> {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const groupId = this.groupId;

    if (!groupId) {
      console.error('groupId is not set.');
      return EMPTY;
    }

    return this.loadTasksByMonth(year, month);
  }

  private ensureTaskList(): void {
    if (!this.groupId || this.taskListFor === this.groupId) return;
    this.taskListFor = this.groupId;
    // Tasks that arrived live before the load are in the pages too.
    this.taskListReady = false;
    this.tasksSubject.next([]);
    this.currentPage = 1;
    this.taskListLoad = this.loadTasks().subscribe();
  }

  loadTasks(): Observable<any[]> {
    if (this.isLoading) {
      return EMPTY;
    }
    this.isLoading = true;
    return this.getTasksByGroup(this.groupId, this.currentPage, this.pageSize).pipe(
      mergeMap((res: any) => {
        const items = res.data;
        const current = this.tasksSubject.getValue();
        const updated = [...current, ...items];
        this.taskListReady = true;
        this.tasksSubject.next(updated);
        this.processItems(updated);
        if (items.length === this.pageSize) {
          this.currentPage++;
          this.isLoading = false;
          return this.loadTasks();
        } else {
          this.isLoading = false;
          return of(updated);
        }
      }),
      catchError(error => {
        this.isLoading = false;
        return of([]);
      })
    );
  }

  getTasksByGroup(groupId: string, page: number, pageSize: number): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/tasks/group/${groupId}?page=${page}&pageSize=${pageSize}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }


  loadTasksByMonth(year: number, month: number): Observable<any> {
    const token = localStorage.getItem('token');
    const params = new HttpParams().set('year', year.toString()).set('month', month.toString()).set('groupId', this.groupId);
    return this.http.get<any[]>(`${this.apiUrl}/tasks/calendar/month`, {
      params, headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap((results: any) => {
        const tasks = results ? results.data : [];
        this.monthTasksSubject.next(tasks);
        this.setSelectedMonth(year, month);
      })
    );
  }

  setSelectedMonth(year: number, month: number) {
    this.selectedMonthSubject.next({ year, month });
  }

  processItems(items: any[]) {
    // Format each items date fields
    const formatted = items.map(formatDates);

    // Now update the BehaviorSubjects with formatted data
    this.tasksSubject.next(formatted);
  }


}